// Calendrier éditorial : vues mois / semaine, filtres, drag & drop, liste mobile
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks, isSameMonth, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PostDialog } from "@/components/PostDialog";
import { UserAvatar } from "@/components/UserAvatar";
import { VOIES, PLATEFORMES, STATUTS_PUBLICATION } from "@/lib/constants";
import { formatHeure } from "@/lib/dates";

const FiltreSelect = ({ valeur, onChange, placeholder, options, testId }) => (
  <Select value={valeur || "tous"} onValueChange={(v) => onChange(v === "tous" ? "" : v)}>
    <SelectTrigger className="rounded-full h-9 w-auto min-w-[130px] text-sm" data-testid={testId}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="tous">{placeholder}</SelectItem>
      {options.map(([k, label]) => (
        <SelectItem key={k} value={k}>{label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function CalendarPage() {
  const { user } = useAuth();
  const canEdit = ["admin", "media"].includes(user.role);
  const [vue, setVue] = useState("mois");
  const [dateCourante, setDateCourante] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [filtres, setFiltres] = useState({ voie: "", plateforme: "", responsable_id: "", statut: "" });
  const [dialog, setDialog] = useState(null); // { post } ou { defaultDate }

  const chargerPosts = useCallback(async () => {
    const params = {};
    Object.entries(filtres).forEach(([k, v]) => v && (params[k] = v));
    try {
      const { data } = await api.get("/editorial-posts", { params });
      setPosts(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [filtres]);

  useEffect(() => { chargerPosts(); }, [chargerPosts]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const jours = useMemo(() => {
    const debut = vue === "mois"
      ? startOfWeek(startOfMonth(dateCourante), { weekStartsOn: 1 })
      : startOfWeek(dateCourante, { weekStartsOn: 1 });
    const fin = vue === "mois"
      ? endOfWeek(endOfMonth(dateCourante), { weekStartsOn: 1 })
      : endOfWeek(dateCourante, { weekStartsOn: 1 });
    const liste = [];
    for (let d = debut; d <= fin; d = addDays(d, 1)) liste.push(d);
    return liste;
  }, [dateCourante, vue]);

  const postsDuJour = (jour) => posts.filter((p) => p.date_publication?.slice(0, 10) === format(jour, "yyyy-MM-dd"));

  const naviguer = (sens) =>
    setDateCourante(vue === "mois" ? addMonths(dateCourante, sens) : addWeeks(dateCourante, sens));

  // Drag & drop natif : déplacer une publication vers un autre jour
  const onDrop = async (e, jour) => {
    e.preventDefault();
    const postId = e.dataTransfer.getData("postId");
    if (!postId || !canEdit) return;
    const post = posts.find((p) => p.id === postId);
    const heure = post?.date_publication?.includes("T") ? post.date_publication.split("T")[1] : "12:00";
    const nouvelleDate = `${format(jour, "yyyy-MM-dd")}T${heure}`;
    if (nouvelleDate === post.date_publication) return;
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, date_publication: nouvelleDate } : p)));
    try {
      await api.patch(`/editorial-posts/${postId}`, { date_publication: nouvelleDate });
      toast.success("Publication déplacée");
    } catch (err) {
      toast.error(apiError(err));
      chargerPosts();
    }
  };

  const ChipPost = ({ p, detail = false }) => {
    const voie = VOIES[p.voie];
    const resp = users.find((u) => u.id === p.responsable_id);
    return (
      <button
        draggable={canEdit}
        onDragStart={(e) => e.dataTransfer.setData("postId", p.id)}
        onClick={() => setDialog({ post: p })}
        className="w-full text-left rounded-lg px-2 py-1.5 text-xs transition-transform hover:-translate-y-0.5"
        style={{ background: `${voie?.couleur}1f`, borderLeft: `3px solid ${voie?.couleur}` }}
        data-testid={`calendar-post-${p.id}`}
      >
        <span className="font-medium block truncate">{formatHeure(p.date_publication)} · {p.titre}</span>
        {detail && (
          <span className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Badge className={`${STATUTS_PUBLICATION[p.statut]?.classe} border-0 text-[10px] px-1.5 py-0`}>{STATUTS_PUBLICATION[p.statut]?.label}</Badge>
            <span className="text-muted-foreground">{p.plateformes.map((pl) => PLATEFORMES[pl]).join(", ")}</span>
            {resp && <UserAvatar user={resp} size="sm" />}
          </span>
        )}
      </button>
    );
  };

  const titrePeriode = vue === "mois"
    ? format(dateCourante, "MMMM yyyy", { locale: fr })
    : `Semaine du ${format(startOfWeek(dateCourante, { weekStartsOn: 1 }), "d MMMM yyyy", { locale: fr })}`;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold">Calendrier éditorial</h1>
          <p className="text-muted-foreground text-sm mt-1">Mémoire · Quotidien · Terrain</p>
        </div>
        {canEdit && (
          <Button onClick={() => setDialog({ defaultDate: format(new Date(), "yyyy-MM-dd") })} className="rounded-full gap-1.5" data-testid="new-post-button">
            <Plus className="h-4 w-4" /> Nouvelle publication
          </Button>
        )}
      </div>

      {/* ——— Barre de navigation + filtres ——— */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex items-center gap-1 rounded-full bg-card border border-border p-1">
          <button onClick={() => naviguer(-1)} className="p-1.5 rounded-full hover:bg-muted transition-colors" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="px-2 text-sm font-semibold first-letter:capitalize min-w-[140px] text-center" data-testid="calendar-period-title">{titrePeriode}</span>
          <button onClick={() => naviguer(1)} className="p-1.5 rounded-full hover:bg-muted transition-colors" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <Button variant="outline" size="sm" onClick={() => setDateCourante(new Date())} className="rounded-full h-9">Aujourd'hui</Button>
        <div className="flex rounded-full bg-card border border-border p-1">
          {["mois", "semaine"].map((v) => (
            <button
              key={v}
              onClick={() => setVue(v)}
              className={`px-4 py-1 rounded-full text-sm font-medium capitalize transition-colors ${vue === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              data-testid={`calendar-view-${v}`}
            >
              {v}
            </button>
          ))}
        </div>
        <FiltreSelect valeur={filtres.voie} onChange={(v) => setFiltres({ ...filtres, voie: v })} placeholder="Toutes les voies" options={Object.entries(VOIES).map(([k, v]) => [k, v.label])} testId="filter-voie" />
        <FiltreSelect valeur={filtres.plateforme} onChange={(v) => setFiltres({ ...filtres, plateforme: v })} placeholder="Toutes plateformes" options={Object.entries(PLATEFORMES)} testId="filter-plateforme" />
        <FiltreSelect valeur={filtres.responsable_id} onChange={(v) => setFiltres({ ...filtres, responsable_id: v })} placeholder="Tous responsables" options={users.map((u) => [u.id, u.name])} testId="filter-responsable" />
        <FiltreSelect valeur={filtres.statut} onChange={(v) => setFiltres({ ...filtres, statut: v })} placeholder="Tous statuts" options={Object.entries(STATUTS_PUBLICATION).map(([k, v]) => [k, v.label])} testId="filter-statut" />
        {Object.values(filtres).some(Boolean) && (
          <button onClick={() => setFiltres({ voie: "", plateforme: "", responsable_id: "", statut: "" })} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors" data-testid="filter-reset">
            <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
          </button>
        )}
      </div>

      {/* ——— Grille desktop ——— */}
      <div className="hidden md:block bg-card rounded-2xl border border-border overflow-hidden carte-douce">
        <div className="grid grid-cols-7 border-b border-border">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((j) => (
            <div key={j} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">{j}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {jours.map((jour) => {
            const duJour = postsDuJour(jour);
            const horsMois = vue === "mois" && !isSameMonth(jour, dateCourante);
            return (
              <div
                key={jour.toISOString()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, jour)}
                className={`group border-b border-r border-border/60 p-1.5 space-y-1 ${vue === "mois" ? "min-h-[110px]" : "min-h-[300px]"} ${horsMois ? "bg-muted/30" : ""}`}
                data-testid={`calendar-day-${format(jour, "yyyy-MM-dd")}`}
              >
                <div className="flex items-center justify-between px-1">
                  <span className={`text-xs font-semibold h-6 w-6 flex items-center justify-center rounded-full ${isToday(jour) ? "bg-primary text-primary-foreground" : horsMois ? "text-muted-foreground/50" : "text-muted-foreground"}`}>
                    {format(jour, "d")}
                  </span>
                  {canEdit && (
                    <button
                      onClick={() => setDialog({ defaultDate: format(jour, "yyyy-MM-dd") })}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded-full hover:bg-muted transition-opacity"
                      data-testid={`calendar-day-add-${format(jour, "yyyy-MM-dd")}`}
                    >
                      <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
                {duJour.map((p) => <ChipPost key={p.id} p={p} detail={vue === "semaine"} />)}
              </div>
            );
          })}
        </div>
      </div>

      {/* ——— Liste mobile ——— */}
      <div className="md:hidden space-y-4" data-testid="calendar-mobile-list">
        {jours.filter((j) => postsDuJour(j).length > 0).map((jour) => (
          <div key={jour.toISOString()}>
            <p className={`text-sm font-semibold mb-2 first-letter:capitalize ${isToday(jour) ? "text-primary" : ""}`}>
              {format(jour, "EEEE d MMMM", { locale: fr })}
            </p>
            <div className="space-y-1.5">
              {postsDuJour(jour).map((p) => <ChipPost key={p.id} p={p} detail />)}
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aucune publication sur cette période.</p>}
      </div>

      <PostDialog
        open={Boolean(dialog)}
        onClose={() => setDialog(null)}
        post={dialog?.post}
        defaultDate={dialog?.defaultDate}
        users={users}
        onSaved={chargerPosts}
      />
    </div>
  );
}
